import { hasBannerflowHostedVideo } from '@creative/elements/utils';
import { ImageOptimizerUrlBuilder } from '@creative/image-optimizer';
import { clearDiContainers, diInject, diInjectable, registerDependency } from '@di/di';
import { AdEvents } from '@domain/ad/ad-events';
import { addColorSchemeMetaTag, emptyIframeSrc } from '@studio/utils/ad/dom';
import { cloneDeep } from '@studio/utils/clone';
import { isBannerflowEnvironment } from '@studio/utils/environment';
import { FontLoader, getFontBlobUrl, injectFontStyle } from '@studio/utils/font-loading';
import { _performanceMark } from '@studio/utils/performance';
import { SafeEval } from '@studio/utils/safe-eval';
import { concatUrl, isRelativeUrl, isUrl, parameterToBool, replaceOrigin } from '@studio/utils/url';
import { AnimatedCreativeAdApi } from './animated-creative-ad-api';
import { registerFeaturesByFiles } from './animated-creative.dependencies';
import { getCustomFPSInterval, isElementVisibleAtTime } from './animation.utils';
import { Animator } from './animator';
import { hasFeededContent, isFeedValue } from './elements/feed/feeds.utils';
import { createRichTextFromString, isVariableSpan, isVersionedWidgetText } from './elements/rich-text/utils';
import { FONT_SERVICE_TEXT_PARAMETER, getFontServiceParamsFromString } from './font-service.utils';
import { CreativeDataNode, isDynamicCreativeElement, isImageNode, isTextNode, isVideoNode, isWidgetNode } from './nodes';
import { Renderer } from './renderer';
import { getParsedCreativeData, mapAssets } from './serialization/element-parser';
export class AnimatedCreative {
    get _window() {
        var _a;
        return (this._inSandboxedIframe ? window : (_a = this._iframe) === null || _a === void 0 ? void 0 : _a.contentWindow) || undefined;
    }
    get _document() {
        var _a;
        const document = (_a = this._window) === null || _a === void 0 ? void 0 : _a.document;
        if (!document) {
            throw new Error('No document found');
        }
        return document;
    }
    constructor(_ad, _creativeData, _preloadAssets, _creativeConfig, _onLoadCallback) {
        this._ad = _ad;
        this._creativeData = _creativeData;
        this._preloadAssets = _preloadAssets;
        this._creativeConfig = _creativeConfig;
        this._onLoadCallback = _onLoadCallback;
        this._preloadedFeeds = new Set();
        this._inSandboxedIframe = false;
        this._destroyed = false;
        this._dynamicElementMap = new Map();
        /**
         * When iframe window is ready to use, init creative
         */
        this._init = async () => {
            console.debug('[AnimatedCreative] init');
            _performanceMark('ac:init:start');
            const contentWindow = this._window;
            if (!contentWindow) {
                throw new Error('Ad container is not visible');
            }
            if (this._destroyed) {
                return;
            }
            FontLoader.setBrowserDocument(this._document);
            FontLoader.setEnvs(this._creativeConfig);
            this._insertCreativeStyles();
            this._rootElement = this._getRootElement();
            registerDependency(0 /* Token.AD */, this._ad);
            diInjectable(3 /* Token.RENDERER */, Renderer).withArgs(this._creativeDataNode, {
                contentWindow,
                fps: this._ad.parameters.fps,
                responsive: parameterToBool(this._ad.parameters.responsive)
                    ? {
                        enabled: true,
                        mode: this._ad.parameters['responsive-mode']
                    }
                    : undefined,
                // Preloaded in _preloadFonts
                fontFamilies: []
            });
            diInjectable(5 /* Token.ANIMATOR */, Animator);
            console.debug('[AnimatedCreative] loading feature files / init IO / SafeEval');
            _performanceMark('ac:init:load:features:start');
            await Promise.all([
                registerFeaturesByFiles(this._ad.selectedCreative.animated.files, this._ad.getOrigin(), this._ad.data.brand.id),
                ImageOptimizerUrlBuilder.init_m({
                    imageOptimizerOrigin: this._creativeConfig.origins.IMAGE_OPTIMIZER || '',
                    svgImageOptimizerOrigin: this._creativeConfig.origins.SVG_IMAGE_OPTIMIZER || '',
                    defaultOrigin: this._ad.selectedCreative.animated.defaultOrigin
                }),
                SafeEval.init({ isSandboxedIframe: this._inSandboxedIframe })
            ]);
            console.debug('[AnimatedCreative] loaded feature files / init IO / SafeEval');
            _performanceMark('ac:init:load:features:end');
            this._creativeDataNode.startTime = this._getStartTime();
            this._applyFrameContentOptimization();
            this.renderer = diInject(3 /* Token.RENDERER */);
            this.feedStore = diInject(6 /* Token.FEED_STORE */, { optional: true });
            this.renderer.on('click', this._onClick);
            this.initDynamicPropertyElements();
            if (this.feedStore) {
                _performanceMark('ac:init:load:feeds:start');
                await this._preloadFeeds();
                _performanceMark('ac:init:load:feeds:end');
            }
            this.animator = diInject(5 /* Token.ANIMATOR */);
            this._initEventBubbling();
            this._registerAdApi();
            _performanceMark('ac:init:render:start');
            // Viewtree has to be initialized before preloading fonts, images and videos
            this.renderer.initialize(this._rootElement, this._creativeDataNode.startTime, true);
            _performanceMark('ac:init:render:end');
            // Note: We have to rerender to include the fonts
            // TODO: Remove this hack in the future. when we rewrite renderer to have loadAssets method.
            _performanceMark('ac:init:rerender:start');
            this.renderer.rerender_m(this._creativeDataNode);
            _performanceMark('ac:init:rerender:end');
            try {
                console.debug('[AnimatedCreative] preloading fonts and elements');
                _performanceMark('ac:preload:fonts:start');
                _performanceMark('ac:preload:elements:start');
                const fontPromises = this._getPreloadFontPromises();
                const elementPromises = [...this.renderer.preloadingElements.values()];
                const preloadPromises = [
                    Promise.all(fontPromises).then(() => {
                        console.debug('[AnimatedCreative] fonts loaded');
                        _performanceMark('ac:preload:fonts:end');
                    }),
                    Promise.all(elementPromises).then(() => {
                        console.debug('[AnimatedCreative] elements loaded');
                        _performanceMark('ac:preload:elements:end');
                    })
                ];
                // not supported in all browsers
                if (Promise.allSettled) {
                    const results = await Promise.allSettled(preloadPromises);
                    console.debug('[AnimatedCreative] loaded elements');
                    results.forEach(result => {
                        if (result.status === 'rejected') {
                            throw result.reason;
                        }
                    });
                }
                else {
                    await Promise.all(preloadPromises);
                }
                _performanceMark('ac:init:load:elements:end');
            }
            catch (err) {
                if (!err) {
                    console.error('`undefined` error occurred while preloading elements');
                    return;
                }
                const error = err;
                if (error.message) {
                    this._handleError(error.message, {
                        image: error.url ? { url: error.url } : undefined,
                        originalError: err
                    });
                }
                else {
                    console.error(err);
                }
            }
            this._checkForWidgetElementNodes();
            if (this.renderer.WidgetRenderer) {
                try {
                    console.debug('[AnimatedCreative] loading widgets');
                    _performanceMark('ac:init:load:widgets:start');
                    // Ugly workaround to ensure that all widgets has completely loaded to ensure that they are in sync with the creative
                    await Promise.all(this.renderer.WidgetRenderer.widgetInitializingPromises);
                    await Promise.all(this.renderer.WidgetRenderer.widgetLoadingPromises);
                }
                catch (err) {
                    let message = 'Unknown error.';
                    let contexts;
                    if (err instanceof Error) {
                        message = err.message;
                        contexts = err.contexts;
                    }
                    if (typeof err === 'string') {
                        message = err;
                    }
                    this._handleError(message, contexts);
                }
                finally {
                    console.debug('[AnimatedCreative] loaded widgets');
                    _performanceMark('ac:init:load:widgets:end');
                }
            }
            // re-render text elements after all fonts are loaded to apply fonts and resize text if needed
            this.renderer.rerenderText_m();
            // Sometimes iframe is hidden (display: none), for on-site banners. Where the publisher have
            // implemented media queries to show and hide multiple banners to simulate responsiveness.
            // Hidden loaded banners have the fallback, that they have zero height when inspected, which
            // currently affects text rendering. So, we have to wait until it is shown to rerender.
            _performanceMark('ac:init:visibility:check');
            if (!this._isPixelVisible()) {
                console.debug('[AnimatedCreative] not visible. Adding visibility listener');
                this._pixelVisibleInterval = contentWindow.setInterval(() => {
                    var _a;
                    if (this._isPixelVisible()) {
                        console.debug('[AnimatedCreative] became visible');
                        (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.rerender_m(this._creativeDataNode);
                        this._showIframe();
                        if (Animator) {
                            this._initPlayback();
                        }
                        contentWindow.clearInterval(this._pixelVisibleInterval);
                    }
                }, 10);
            }
            else {
                this._showIframe();
                if (Animator) {
                    this._initPlayback();
                }
            }
            if (typeof this._onLoadCallback === 'function') {
                this._onLoadCallback(this);
            }
            _performanceMark('ac:init:end');
        };
        /**
         * When user clicks the creative
         */
        this._onClick = (clickEvent) => {
            let event;
            let deepLinkUrl = '';
            if (!(clickEvent instanceof MouseEvent)) {
                event = clickEvent.event;
                deepLinkUrl = clickEvent.deepLinkUrl;
            }
            else {
                event = clickEvent;
            }
            const url = this._getClickUrl();
            if (!url) {
                return;
            }
            if (deepLinkUrl) {
                // we removed the u property from click tracking is the deepLink still needed for something else?
                this._ad.click({ x: event.x, y: event.y, deepLinkUrl });
            }
            else {
                this._ad.click(event);
            }
        };
        console.debug('[AnimatedCreative] construct');
        this._setEnvironmentAndOrigins();
        try {
            const data = this._prepareData(_creativeData);
            this._creativeDataNode = new CreativeDataNode(data);
        }
        catch (e) {
            throw new Error(`Could not prepare creative data: ${e === null || e === void 0 ? void 0 : e.message}`);
        }
        try {
            const iframe = this._createIframe();
            console.debug('[AnimatedCreative] created iframe');
            const contentWindow = iframe.contentWindow;
            this._inSandboxedIframe =
                !iframe.contentDocument || !!parameterToBool(this._ad.parameters['iframeless']);
            if (this._inSandboxedIframe) {
                console.debug('[AnimatedCreative] is sandboxed. Removing iframe');
                iframe.remove();
                this._init();
            }
            else if (contentWindow) {
                addColorSchemeMetaTag(iframe);
                this._iframe = iframe;
                // Wait for iframeWindow to be available
                this._onIframeWindowLoad(contentWindow, this._init);
            }
            else {
                console.warn('Did not initialize the creative, due to container element is not visible:\n', this._ad.container);
                return;
            }
        }
        catch (e) {
            throw new Error(`Could not initialize creative: ${e === null || e === void 0 ? void 0 : e.message}`);
        }
    }
    getTrackerPlaythroughData() {
        if (!this.animator) {
            throw new Error('Animator is not initialized');
        }
        const trackingElements = this._creativeDataNode.elements.filter(el => isVideoNode(el) || isWidgetNode(el));
        const videoStorage = this._creativeConfig.origins.VIDEOS_STORAGE;
        const playTime = this.animator.time + this.animator.duration * (this.animator.loop - 1);
        return {
            loop: this.animator.loop,
            playTime,
            progress: Math.min(playTime / this.animator.duration, 1),
            isBannerflowHosted: Number(trackingElements.some(el => hasBannerflowHostedVideo(el, videoStorage))),
            elements: [
                ...trackingElements.reduce((tags, el) => {
                    let tag;
                    if (isWidgetNode(el)) {
                        tag = 'w';
                    }
                    else if (isVideoNode(el)) {
                        tag = 'v';
                    }
                    else if (isImageNode(el)) {
                        tag = 'i';
                    }
                    else if (isTextNode(el)) {
                        tag = 't';
                    }
                    if (tag) {
                        if (hasFeededContent(el)) {
                            tags.add(`${tag}f`);
                        }
                        else {
                            tags.add(tag);
                        }
                    }
                    return tags;
                }, new Set())
            ]
        };
    }
    _getPreloadFonts() {
        return this._preloadAssets.filter(asset => asset.type === 'font');
    }
    async updateDynamicPropertyElements_m(dynamicProperties) {
        const getElementUpdates = ({ creativeElement, dataNode }) => {
            if (isWidgetNode(creativeElement)) {
                return creativeElement.customProperties
                    .filter(prop => prop.dynamicPropertyId)
                    .map(prop => {
                    const dynamicProperty = dynamicProperties.find(({ id }) => id === prop.dynamicPropertyId);
                    return this._updateDynamicPropertyElement(creativeElement, dataNode, dynamicProperty);
                });
            }
            else {
                const dynamicProperty = dynamicProperties.find(({ id }) => id === creativeElement.dynamicPropertyId);
                return [this._updateDynamicPropertyElement(creativeElement, dataNode, dynamicProperty)];
            }
        };
        const updatePromises = [...this._dynamicElementMap.values()].flatMap(getElementUpdates);
        await Promise.all(updatePromises);
    }
    _updateDynamicPropertyElement(creativeElement, node, dynamicProperty) {
        var _a, _b, _c;
        if (!this.renderer) {
            throw new Error('Renderer not yet initialized.');
        }
        const value = dynamicProperty && 'value' in dynamicProperty ? dynamicProperty.value : undefined;
        if (isTextNode(node) && isTextNode(creativeElement)) {
            if (typeof value === 'string') {
                const newText = createRichTextFromString(value);
                node.content.spans = newText.spans;
            }
            else {
                node.content = cloneDeep(creativeElement.content);
            }
        }
        else if (isImageNode(node) && isImageNode(creativeElement)) {
            if (!node.imageAsset) {
                throw new Error(`Image node with ID '${node.id}' has no image asset.`);
            }
            if (typeof value === 'string') {
                node.imageAsset.url = value;
            }
            else {
                node.imageAsset.url = (_b = (_a = creativeElement.imageAsset) === null || _a === void 0 ? void 0 : _a.url) !== null && _b !== void 0 ? _b : '';
            }
        }
        else if (isWidgetNode(node) && isWidgetNode(creativeElement)) {
            if (typeof value === 'string') {
                const dynamicCustomProperty = creativeElement.customProperties.find(prop => prop.dynamicPropertyId === (dynamicProperty === null || dynamicProperty === void 0 ? void 0 : dynamicProperty.id));
                const nodeProperty = node.customProperties.find(({ name }) => name === (dynamicCustomProperty === null || dynamicCustomProperty === void 0 ? void 0 : dynamicCustomProperty.name));
                if (nodeProperty && isVersionedWidgetText(nodeProperty)) {
                    nodeProperty.value.text = value;
                    nodeProperty.versionPropertyId = undefined;
                }
            }
            else {
                node.customProperties = cloneDeep(creativeElement.customProperties);
            }
        }
        this.renderer.preloadingElements.delete(creativeElement.id);
        this.renderer.rerender_m(this._creativeDataNode);
        return (_c = this.renderer.preloadingElements.get(creativeElement.id)) !== null && _c !== void 0 ? _c : Promise.resolve();
    }
    _getRootElement() {
        if (this._inSandboxedIframe) {
            return this._ad.container;
        }
        else {
            return this._document.body;
        }
    }
    /**
     * @summary
     * Check pixel visibilty.
     *
     * @description
     * The ability to render visible pixels on the banner. Notice, this API is different from
     * DOM visibility, where display none is still counted as visibly. We have manually confirmed
     * that display none:ed DOM subtree's are cheap to check bounding rect.
     */
    _isPixelVisible() {
        const testElement = this._document.createElement('div');
        const style = testElement.style;
        style.height = '1px';
        style.width = '1px';
        style.position = 'absolute';
        style.top = '-100000px';
        this._ad.container.appendChild(testElement);
        const result = testElement.getBoundingClientRect().height > 0;
        this._ad.container.removeChild(testElement);
        return result;
    }
    _checkForWidgetElementNodes() {
        if (!this.renderer) {
            return;
        }
        for (const element of this.renderer.creativeDocument.elements) {
            if (isWidgetNode(element)) {
                this.hasWidgetElements = true;
                break;
            }
        }
    }
    _initPlayback() {
        _performanceMark('ac:animator:start');
        if (!this.animator) {
            return;
        }
        const autoplay = this._ad.shouldAutoplay();
        const fpsInterval = getCustomFPSInterval(this._ad.parameters.fps);
        if (fpsInterval) {
            this.animator.updateCustomFPSInterval(fpsInterval);
        }
        if (this._creativeConfig.environment === 5 /* AdEnvironment.VideoGenerator */) {
            this._creativeDataNode.loops = 1;
        }
        // Play/pause creative when it leaves or enter view
        if (this._ad.viewability.pauseOutsideView()) {
            this._addOutOfViewEvents(autoplay);
        }
        else if (autoplay) {
            console.debug('[AnimatedCreative] autoplaying');
            this.animator.play();
        }
        this._ad.on(AdEvents.Stop, () => {
            var _a;
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.pause();
        });
        _performanceMark('ac:animator:end');
    }
    _addOutOfViewEvents(autoplay) {
        var _a;
        this._ad.on(AdEvents.ViewEnter, () => {
            var _a;
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.play();
        });
        this._ad.on(AdEvents.ViewLeave, () => {
            var _a;
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.pause();
        });
        if (this._ad.viewability.visible && autoplay) {
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.play();
        }
    }
    _getStartTime() {
        var _a;
        const seek = parseFloat(((_a = this._ad.parameters) === null || _a === void 0 ? void 0 : _a.seek) || '');
        // Seek parameter is added to the tag
        if (!isNaN(seek)) {
            // VRS sets "seek" to -1
            return Math.max(0, seek);
        }
        // Used by image generator to capture preload image at the right place in timeline
        if (this._shouldStartFromPreloadFrame()) {
            const preloadFrame = this._creativeDataNode.preloadImage.frames[0];
            // Defaults to 1 if not set
            return typeof preloadFrame === 'number' ? preloadFrame : 1;
        }
        else {
            return this._creativeDataNode.startTime || 0;
        }
    }
    _shouldStartFromPreloadFrame() {
        var _a;
        return ((_a = this._ad.parameters) === null || _a === void 0 ? void 0 : _a.seek) === 'preload-frame';
    }
    _setEnvironmentAndOrigins() {
        registerDependency(2 /* Token.CREATIVE_CONFIG */, this._creativeConfig);
        if (isBannerflowEnvironment(this._creativeConfig.environment)) {
            return;
        }
        const origin = this._ad.getOrigin();
        if (origin) {
            // if the ad is served from a custom domain, we need to fix all origins
            const origins = this._creativeConfig.origins;
            for (const k in origins) {
                const key = k;
                const value = origins[key];
                if (!isUrl(value)) {
                    continue;
                }
                if (isRelativeUrl(value)) {
                    origins[key] = concatUrl(origin, value);
                }
                else if (!origin.includes('bannerflow.com/preview')) {
                    // don't replace origin for ACG hosted ads in production
                    origins[key] = replaceOrigin(value, origin);
                }
            }
        }
    }
    /**
     * Create wrapping iframe.
     * Note that in this stage we cannot insert content into the iframe in all browers (FF)
     */
    _createIframe() {
        const iframe = document.createElement('iframe');
        // Dimension hacks to fix iframe issues in iOS:
        // https://stackoverflow.com/a/20142280
        const style = iframe.style;
        style.width = style.height = '1px';
        style.minWidth = style.maxWidth = style.minHeight = '100%';
        style.outline = style.border = style.transform = style.filter = 'none';
        style.display = 'block';
        style.position = 'absolute';
        style.left = style.top = style.opacity = '0';
        style.transformOrigin = '0 0';
        iframe.src = this._ad.selectedCreative.id === '7705683' ? 'about:blank' : emptyIframeSrc();
        iframe.scrolling = 'no';
        if (this._ad.shouldFadeIn()) {
            style.transition = 'opacity 0.2s ease';
        }
        this._ad.container.appendChild(iframe);
        return iframe;
    }
    _showIframe() {
        console.debug('[AnimatedCreative] showing iframe');
        _performanceMark('ac:iframe:show');
        if (this._iframe) {
            this._iframe.style.opacity = '1';
        }
    }
    /**
     * Needs to be executed AFTER the document is ready
     */
    _insertCreativeStyles() {
        const url = this._getClickUrl();
        const cursor = url ? 'pointer;' : 'default;';
        const doc = this._document;
        const style = doc.createElement('style');
        style.innerHTML = `*{box-sizing: border-box;}:root{color-scheme: light only;}html,body{margin:0;padding:0;overflow:hidden;width:100%;height:100%;-ms-user-select:none;-moz-user-select:none;-webkit-user-select:none;user-select:none;cursor:${cursor};}`;
        this._creativeStylesRef = style;
        doc.head.appendChild(style);
    }
    _getClickUrl() {
        const redirectUrl = this._ad.redirect.getRedirectUrl();
        if (redirectUrl === false) {
            return;
        }
        return redirectUrl || this._ad.redirect.getTargetUrl();
    }
    /**
     * When iframe window is ready
     * @param callback
     */
    _onIframeWindowLoad(iframeWindow, callback) {
        console.debug('[AnimatedCreative] waiting for iframe');
        if (iframeWindow.document.readyState === 'complete') {
            setTimeout(() => {
                callback();
            }, 100);
        }
        else {
            const onLoad = () => {
                if (callback) {
                    callback();
                }
                if (iframeWindow) {
                    iframeWindow.removeEventListener('load', onLoad);
                }
            };
            iframeWindow.addEventListener('load', onLoad);
        }
    }
    _initEventBubbling() {
        const { animator: animator, _ad: ad, renderer: renderer } = this;
        if (!(animator && ad && renderer)) {
            throw new Error('Failed to init event bubbling. Animator, Ad or Renderer is missing');
        }
        console.debug('[AnimatedCreative] subscribing to animator events');
        animator.on('loop', () => ad.emit(AdEvents.Loop, this._getAnimatorEventData()));
        animator.on('pause', () => ad.emit(AdEvents.Pause, this._getAnimatorEventData()));
        animator.on('play', () => ad.emit(AdEvents.Play, this._getAnimatorEventData()));
        animator.on('stop', () => ad.emit(AdEvents.Stop, this._getAnimatorEventData()));
        animator.on('seek', () => ad.emit(AdEvents.Seek, this._getAnimatorEventData()));
        renderer.on('mute', (muted) => ad.emit(AdEvents.Mute, muted));
        _performanceMark('ac:animated-events:added');
    }
    _getAnimatorEventData() {
        const animator = this.animator;
        return {
            ad: this._ad,
            time: (animator === null || animator === void 0 ? void 0 : animator.time) || 0,
            loop: (animator === null || animator === void 0 ? void 0 : animator.loop) || 0
        };
    }
    _registerAdApi() {
        console.debug('[AnimatedCreative] patching ad api');
        if (!this.animator || !this.renderer) {
            return;
        }
        this._ad.adTag.adApi = new AnimatedCreativeAdApi({
            animatedCreative: this,
            creativeData: this._creativeData,
            creativeDataNode: this._creativeDataNode
        });
    }
    destroy() {
        var _a, _b, _c;
        clearInterval(this._pixelVisibleInterval);
        (_a = this._rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', this._onClick);
        this._destroyed = true;
        try {
            if (this._creativeStylesRef) {
                this._document.head.removeChild(this._creativeStylesRef);
                this._creativeStylesRef = undefined;
            }
            // eslint-disable-next-line no-empty
        }
        catch (_d) { }
        (_b = this.renderer) === null || _b === void 0 ? void 0 : _b.destroy();
        (_c = this.animator) === null || _c === void 0 ? void 0 : _c.destroy();
        clearDiContainers();
    }
    /**
     * Preloads all feeds as well as fonts that are used in conjunction as character styling
     */
    async _preloadFeeds() {
        _performanceMark('ac:preload:feeds:start');
        if (!this.feedStore) {
            return;
        }
        this._creativeDataNode.elements.forEach(child => {
            const isElementWithFeedAtRoot = isImageNode(child) || isVideoNode(child);
            if (isTextNode(child)) {
                for (const span of child.content.spans) {
                    if (isVariableSpan(span)) {
                        this._preloadedFeeds.add(span.style.variable.id);
                    }
                }
            }
            else if (child.feed && isElementWithFeedAtRoot) {
                this._preloadedFeeds.add(child.feed.id);
            }
            else if (this._ad.overriddenFeedData && isWidgetNode(child)) {
                child.customProperties
                    .filter(property => property.unit === 'feed')
                    .forEach(feedProperty => {
                    try {
                        const feed = feedProperty.value;
                        if (!isFeedValue(feed) || feed.id.length === 0) {
                            return;
                        }
                        this._preloadedFeeds.add(feed.id);
                    }
                    catch (_a) {
                        /* empty */
                    }
                });
            }
        });
        // Override the first feed, used for example with DCO
        // DCO is only allowed to have 1 feed
        if (this._ad.overriddenFeedData && this._preloadedFeeds.size > 0) {
            this.feedStore.feeds.set([...this._preloadedFeeds][0], {
                feed: {
                    data: this._ad.overriddenFeedData,
                    intervalInSeconds: 0
                },
                interval: undefined,
                overridden: true
            });
        }
        await this.feedStore.preloadFeeds([...this._preloadedFeeds]);
        _performanceMark('ac:preload:feeds:end');
    }
    _getPreloadFontPromises() {
        const preloadFonts = this._getPreloadFonts();
        if (this._creativeConfig.environment === 6 /* AdEnvironment.Feed */) {
            return preloadFonts.map(font => FontLoader.loadFontFace(Object.assign(Object.assign({}, font.value), { src: getFontBlobUrl(font.value.src) })));
        }
        if (this.feedStore) {
            for (const element of this.renderer.creativeDocument.elements) {
                if (isTextNode(element)) {
                    for (const span of element.content.spans) {
                        if (isVariableSpan(span)) {
                            this.feedStore.addFeedElement(element.id, span.style.variable, element);
                        }
                    }
                }
            }
            for (const id of this._preloadedFeeds) {
                // Adds to fontCharacterCache and uses preload below if in output
                this.feedStore.loadFontsBasedOnFeedData(id, true);
            }
        }
        return preloadFonts.map(({ value: font }) => font && this._preloadFont(font)).filter(Boolean);
    }
    async _preloadFont(font, newContent = '') {
        var _a;
        if (!font.src.startsWith('data')) {
            const [url, encodedChars] = font.src.split(FONT_SERVICE_TEXT_PARAMETER);
            const feedCharacters = ((_a = this.feedStore) === null || _a === void 0 ? void 0 : _a.fontCharacterCache.get(font.id)) || '';
            const textCharacters = decodeURIComponent(encodedChars);
            const { textParams, distinctText } = getFontServiceParamsFromString(feedCharacters + textCharacters + newContent);
            if (feedCharacters && this.feedStore) {
                this.feedStore.fontCharacterCache.set(font.id, distinctText);
            }
            if (newContent || feedCharacters) {
                font.src = url + textParams;
            }
            // Don't load the font if no characters are included
            if (!distinctText) {
                return;
            }
        }
        try {
            const fallbackFontInjection = this._creativeConfig.environment === 2 /* AdEnvironment.Preview */ &&
                this._ad.parameters['font-loading'];
            if (!fallbackFontInjection) {
                await FontLoader.loadFontFace(font, this._creativeConfig.origins.FONTSERVICE);
            }
            else {
                injectFontStyle(this._document, font, this._creativeConfig.origins.FONTSERVICE);
                await this._document.fonts.load(`1em f-${font.id}`); // resolves once the passed font family is loaded
            }
        }
        catch (err) {
            /* Do nothing */
            this._handleError('Could not load font', {
                font,
                originalError: err
            });
        }
    }
    _handleError(message, data) {
        // propagating animated creative errors to the ad
        this._ad.emit(AdEvents.Error, { event: message, data });
    }
    _prepareData(serializedCreativeData) {
        console.debug('[AnimatedCreative] parsing preload assets');
        mapAssets(serializedCreativeData.elements, this._preloadAssets);
        return getParsedCreativeData(serializedCreativeData);
    }
    initDynamicPropertyElements() {
        var _a;
        for (const element of this._creativeData.elements) {
            if (isDynamicCreativeElement(element)) {
                const dataNode = this._creativeDataNode.findNodeById_m(element.id);
                if (!dataNode) {
                    return;
                }
                this._dynamicElementMap.set(element.id, {
                    creativeElement: element,
                    dataNode: dataNode
                });
                if (isImageNode(element)) {
                    // Adding the element ID to dynamicImageElementIds ensures its cropping parameters are skipped.
                    (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.dynamicImageElementIds.add(element.id);
                }
            }
        }
    }
    _parseAdditionalFrameTimes() {
        const additionalFrames = this._ad.parameters['additional-frames'];
        if (!(additionalFrames === null || additionalFrames === void 0 ? void 0 : additionalFrames.trim())) {
            return [];
        }
        return additionalFrames
            .split(',')
            .map(timeStr => parseFloat(timeStr.trim()))
            .filter(time => !isNaN(time) && time >= 0);
    }
    /**
     * CIRS optimization: Hides elements that are not visible at start time or additional frames
     */
    _applyFrameContentOptimization() {
        var _a;
        if (this._creativeConfig.environment !== 4 /* AdEnvironment.ImageGenerator */ ||
            !parameterToBool(this._ad.parameters['frame-content-only'])) {
            return;
        }
        const additionalFrameTimes = this._parseAdditionalFrameTimes();
        const startTime = (_a = this._creativeDataNode.startTime) !== null && _a !== void 0 ? _a : 0;
        const relevantTimes = [startTime, ...additionalFrameTimes];
        for (const element of this._creativeDataNode.elements) {
            const isVisibleAtAnyRelevantTime = relevantTimes.some(time => isElementVisibleAtTime(element, time));
            if (!isVisibleAtAnyRelevantTime) {
                element.hidden = true;
            }
        }
    }
}
